import { scheduleResourcePreload } from '@everyturn/browser-runtime/loading';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  buttonVariants,
  cn,
  toast,
  Toaster,
} from '@workspace/app-components';
import { Loader2 } from 'lucide-react';
import { lazy, Suspense, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createHashRouter, RouterProvider, Outlet, Navigate, Link, useLocation, useRouteError } from 'react-router';
import { AppShell } from '@/components/AppShell';
import { GameArrival } from '@/components/GameArrival';
import { GameArrivalContext } from '@/hooks/use-game-arrival';
import { PlayModeProvider } from '@/hooks/use-play-mode';
import { diagnostics, supportReference } from '@/lib/diagnostics';
import { gameplayLoader } from '@/lib/gameplay-loader';
import { LandingPage } from '@/routes/LandingPage';
import { useAuthStore } from '@/stores/auth-store';

const HistoryPage = lazy(() => import('@/routes/HistoryPage').then((module) => ({ default: module.HistoryPage })));
const PlayPage = lazy(() => import('@/routes/PlayPage').then((module) => ({ default: module.PlayPage })));
const ProfilePage = lazy(() => import('@/routes/ProfilePage').then((module) => ({ default: module.ProfilePage })));
const TablePage = lazy(() => import('@/routes/TablePage').then((module) => ({ default: module.TablePage })));
const PrivacyPage = lazy(() => import('@/routes/LegalPage').then((module) => ({ default: module.PrivacyPage })));
const TermsPage = lazy(() => import('@/routes/LegalPage').then((module) => ({ default: module.TermsPage })));

const pageTitles: Record<string, string> = {
  '/': 'CardForest – Classic Card Games Online',
  '/play': 'Play – CardForest',
  '/profile': 'Profile – CardForest',
  '/history': 'History – CardForest',
  '/privacy': 'Privacy Policy (Draft) – EveryTurn',
  '/terms': 'Terms of Service (Draft) – EveryTurn',
};

function RouteLoading() {
  return (
    <div className="flex h-full items-center justify-center p-6">
      <p role="status" className="flex items-center gap-2">
        <Loader2 aria-hidden="true" className="size-5 animate-spin" />
        Loading page...
      </p>
    </div>
  );
}

function RouteError() {
  const error = useRouteError();
  const reportArrival = useContext(GameArrivalContext);
  useLayoutEffect(() => reportArrival?.('error'), [reportArrival]);
  const [reference, setReference] = useState<string>();
  const reported = useRef<{ error: unknown; reference: string } | null>(null);
  useEffect(() => {
    if (reported.current && reported.current.error === error) return;
    console.error('[Page] Route failed:', error);
    const reference = diagnostics.report('route_failed', error);
    reported.current = { error, reference };
    setReference(reference);
  }, [error]);
  return (
    <div className="mx-auto max-w-lg p-6">
      <Alert variant="destructive">
        <AlertTitle>This page is unavailable</AlertTitle>
        <AlertDescription>
          <p>Check your connection and reload the page. If the problem continues, return home and report it.</p>
          {reference && <p className="break-words">{supportReference(reference)}</p>}
          <div className="flex flex-wrap gap-2">
            <Button className="min-h-11" onClick={() => window.location.reload()}>
              Reload page
            </Button>
            <Link to="/" className={cn(buttonVariants({ variant: 'outline' }), 'min-h-11')}>
              Return home
            </Link>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}

function AuthGuard() {
  const user = useAuthStore((s) => s.user);
  const loading = useAuthStore((s) => s.loading);
  const isGuest = user?.provider === 'guest';
  if (loading) return <RouteLoading />;
  if (!user || isGuest) return <Navigate to="/" replace />;
  return <Outlet />;
}

function Layout() {
  const { pathname, key } = useLocation();

  useEffect(() => {
    document.title = pageTitles[pathname] ?? 'CardForest – Classic Card Games Online';
  }, [pathname]);

  return (
    <PlayModeProvider>
      <div id="app" data-active-path={pathname}>
        <AppShell>
          {pathname === '/play' ? (
            <GameArrival key={key}>
              <Suspense fallback={null}>
                <Outlet />
              </Suspense>
            </GameArrival>
          ) : (
            <Suspense key={pathname} fallback={<RouteLoading />}>
              <Outlet />
            </Suspense>
          )}
        </AppShell>
        <Toaster position="top-left" />
      </div>
    </PlayModeProvider>
  );
}

const router = createHashRouter([
  {
    element: <Layout />,
    children: [
      { index: true, element: <LandingPage /> },
      { path: 'play', element: <PlayPage />, errorElement: <RouteError /> },
      { path: 'tables/:tableId', element: <TablePage />, errorElement: <RouteError /> },
      { path: 'privacy', element: <PrivacyPage />, errorElement: <RouteError /> },
      { path: 'terms', element: <TermsPage />, errorElement: <RouteError /> },
      {
        element: <AuthGuard />,
        children: [
          { path: 'profile', element: <ProfilePage />, errorElement: <RouteError /> },
          { path: 'history', element: <HistoryPage />, errorElement: <RouteError /> },
        ],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);

export function App() {
  useEffect(
    () =>
      scheduleResourcePreload([gameplayLoader.runtime, gameplayLoader.renderer], (error) => {
        console.warn('[Gameplay preload] Shared resources could not be warmed:', error);
      }),
    [],
  );
  const signOutWarning = useAuthStore((s) => s.signOutWarning);
  const startSessionSync = useAuthStore((s) => s.startSessionSync);

  useEffect(startSessionSync, [startSessionSync]);
  useEffect(() => {
    if (signOutWarning) toast.warning(signOutWarning, { id: 'auth-signout-warning' });
    else toast.dismiss('auth-signout-warning');
  }, [signOutWarning]);

  return <RouterProvider router={router} />;
}
